<ppl-app-container [showCopyright]="false">
  <div class="align-in-center">
    <ppl-box-container-with-title [title]="title$ | async"
                                  [description]="description$ | async"
                                  [small]="true"
                                  [ngSwitch]="mode$ | async">
      <ppl-error-box *ngIf="error$ | async as error"
                     [ngSwitch]="error.error">
        <ng-container *ngSwitchCase="TwoFactorError.TOO_MANY_REQUESTS">{{ getRetryAfterMessage(error) }}</ng-container>
        <ng-container *ngSwitchDefault
                      i18n="@@Unknown_error.">Unknown error.</ng-container>
      </ppl-error-box>

      <ng-container *ngSwitchCase="TwoFactorMode.OPTIONS">
        <div class="content"
             [formGroup]="optionsForm">
          <ppl-two-factor-method-selector formControlName="methodId"
                                          [methods]="optionsMethods$ | async"></ppl-two-factor-method-selector>
          <div class="info"
               i18n="@@TwoFactorVerificationSelectOption">Select a 2-step verification method you prefer and press <strong>Continue</strong>.</div>
          <div class="action-buttons">
            <button pplButton
                    class="submit-button"
                    (click)="onOptionsSetTwoFactorMethod()"
                    i18n="@@Continue">Continue</button>
            <button pplLink
                    class="link-button"
                    (click)="onOptionsBackButtonClick()">
              <ng-container *ngTemplateOutlet="backButtonTextTemplate"></ng-container>
            </button>
          </div>
        </div>
      </ng-container>
      <ng-container *ngSwitchCase="TwoFactorMode.SETUP">
        <div class="content">
          <ppl-edit-email [user]="user$ | async"
                          (edit)="onBackToLogin()"></ppl-edit-email>
          <div class="click-to-setup">
            <ng-container *ngIf="currentSetupMethodType$ | async as currentSetupMethodType; else noSetupMethodTypeSelected">
              <ppl-icon [svgIcon]="methodTypeToIconMap[currentSetupMethodType]"
                        height="48px"
                        width="48px"></ppl-icon>
              <div class="setup-button-wrapper">
                <button pplButton
                        class="setup-button"
                        i18n="@@Click_To_Set_Up"
                        (click)="onSetupMethodType(currentSetupMethodType)">Click To Set Up</button>
                <ppl-loading-overlay *ngIf="setuping$ | async"
                                     [visible]="true"
                                     [inverse]="true"></ppl-loading-overlay>
              </div>
            </ng-container>
            <ng-template #noSetupMethodTypeSelected>
              <span i18n="@@No_2-Step_verification_method_selected">No 2-Step verification method selected</span>
            </ng-template>
          </div>
          <div class="info"
               i18n="@@2-Step_verification_was_enabled_to_improve_your_account_security.">2-Step verification was enabled to improve your account security.</div>
          <div class="info"
               i18n="@@TwoFactorSetupClickToSetupText">To continue, <strong>Click To Set Up</strong> and complete a simple verification process.</div>
          <div class="info"
               i18n="@@It_takes_a_few_seconds.">It takes a few seconds.</div>
          <div class="action-buttons">
            <div class="action-buttons-row">
              <button pplLink
                      class="link-button"
                      (click)="onBackButtonClick()">
                <ng-container *ngTemplateOutlet="backButtonTextTemplate"></ng-container>
              </button>
              <button *ngIf="(setupMethodTypes$ | async).length"
                      pplLink
                      class="link-button"
                      [disabled]="setuping$ | async"
                      [pplPopover]="setupMoreOptionsPopover"
                      [pplPopoverAlignEnd]="true"
                      [pplPopoverDisabled]="setuping$ | async"
                      i18n="@@Show_more_options">Show more options</button>
            </div>
          </div>
        </div>
      </ng-container>
      <ng-container *ngSwitchCase="TwoFactorMode.VERIFY">
        <div class="content"
             [formGroup]="verifyForm">
          <ppl-edit-email [user]="user$ | async"
                          (edit)="onBackToLogin()"></ppl-edit-email>
          <div class="verification">
            <ppl-icon class="verification-method-icon"
                      [svgIcon]="methodTypeToIconMap[(currentMethod$ | async)?.type]"
                      height="48px"
                      width="48px"></ppl-icon>
            <div>
              <ppl-input-container label="Verification Code"
                                   i18n-label="@@Verification_Code"
                                   pplFormControlName="code">
                <ppl-two-factor-verification-code formControlName="code"
                                                  (submit)="onVerifyCodeClick()"></ppl-two-factor-verification-code>
              </ppl-input-container>
              <div *ngIf="verifyResendCodeVisible$ | async"
                   class="re-send-code-container">
                <button pplLink
                        class="link-button"
                        [disabled]="verifyResendCodeDisabled$ | async"
                        (click)="onVerifyResendCode()"
                        i18n="@@Re-send_code">Re-send code</button>
                <ppl-icon *ngIf="verifyResendToggleIcon$ | async"
                          svgIcon="checkmark-white"
                          height="14px"
                          width="14px"></ppl-icon>
              </div>
            </div>
          </div>
          <ng-container *ngIf="currentMethod$ | async as currentMethod">
            <div [ngSwitch]="currentMethod.type">
              <div *ngSwitchCase="TwoFAType.BACKUP_CODES"
                   class="info"
                   i18n="@@TwoFactorScreenVerifyBackupCodesInfoMessage">Enter one of the pre-generated {{ TWO_FACTOR_CODE_LENGTH }}-digit backup codes.</div>
              <ng-container *ngSwitchCase="TwoFAType.AUTH_APP">
                <div class="info"
                     i18n="@@TwoFactorScreenVerifyAuthAppInfoMessage">Enter the {{ TWO_FACTOR_CODE_LENGTH }}-digit verification code from your:</div>
                <div class="method-value">{{ currentMethodName$ | async }}</div>
              </ng-container>
              <ng-container *ngSwitchCase="TwoFAType.DUMMY">
                <div class="info"
                     i18n="@@Enter_the_following_code:">Enter the following code:</div>
                <div class="method-value">{{ currentMethodValue$ | async }}</div>
              </ng-container>
              <ng-container *ngSwitchCase="TwoFAType.EMAIL_MS_IDENTITY_CONFIRMATION">
                <div class="info"
                     i18n="@@TwoFactorScreenVerifyDefaultInfoMessage">Enter the {{ TWO_FACTOR_CODE_LENGTH }}-digit verification code sent to:</div>
                <div class="method-value">{{ currentMethodValue$ | async }}</div>
                <div class="info"
                     i18n="@@It_may_take_several_seconds_to_arrive.">It may take several seconds to arrive.</div>
                <div class="info"
                     i18n="@@TwoFactorScreenVerifyEmailMSIdentityConfirmationInfoMessage">This has to be done only the first time you use your Microsoft Account to login.</div>
              </ng-container>
              <ng-container *ngSwitchDefault>
                <div class="info"
                     i18n="@@TwoFactorScreenVerifyDefaultInfoMessage">Enter the {{ TWO_FACTOR_CODE_LENGTH }}-digit verification code sent to:</div>
                <div class="method-value">{{ currentMethodValue$ | async }}</div>
                <div class="info"
                     i18n="@@It_may_take_several_seconds_to_arrive.">It may take several seconds to arrive.</div>
              </ng-container>
            </div>
            <ppl-checkbox *ngIf="!rememberDisabledForTypes.includes(currentMethod.type)"
                          formControlName="remember"
                          i18n="@@Don't_ask_again_on_this_device">Don't ask again on this device</ppl-checkbox>
          </ng-container>
          <div class="action-buttons">
            <button pplButton
                    class="submit-button"
                    (click)="onVerifyCodeClick()"
                    [disabled]="!(verification$ | async)"
                    i18n="@@Continue">Continue</button>
            <div class="action-buttons-row">
              <button pplLink
                      class="link-button"
                      (click)="onBackButtonClick()">
                <ng-container *ngTemplateOutlet="backButtonTextTemplate"></ng-container>
              </button>
              <button *ngIf="(currentMethod$ | async)?.type !== TwoFAType.EMAIL_MS_IDENTITY_CONFIRMATION"
                      pplLink
                      class="link-button"
                      [disabled]="verifyResendCodeDisabled$ | async"
                      (click)="onVerifyMoreOptionClick()">
                <ng-container i18n="@@Try_more_options">Try more options</ng-container> &gt;&gt;
              </button>
            </div>
          </div>
        </div>
      </ng-container>

      <ppl-loading-overlay *ngIf="loading$ | async"
                           [large]="true"
                           [visible]="true"
                           [inverse]="true"></ppl-loading-overlay>
    </ppl-box-container-with-title>
  </div>
</ppl-app-container>

<ng-template #backButtonTextTemplate>
  &lt;&lt; <ng-container i18n="@@Back">Back</ng-container>
</ng-template>

<ng-template #setupMoreOptionsPopover>
  <ppl-two-factor-method-type-selector-dropdown [value]="currentSetupMethodType$ | async"
                                                [types]="setupMethodTypes$ | async"
                                                (valueChange)="onSetupMethodTypeChange($event)"></ppl-two-factor-method-type-selector-dropdown>
</ng-template>
